/* hys-bundle-builder.js
 * Behaviour for the Crew Pack bundle picker (snippets/hys-bundle-builder-widget.liquid).
 * Layout mirrors the FastBundle widget: per-row Add button (which opens a
 * variant popover on multi-variant products) that turns into a quantity
 * stepper, and a total bar (estimated total + selected thumbnails + status).
 *
 * DISPLAY-ONLY CONTRACT: totals and savings shown here are estimates for UX.
 * The authoritative discount is applied server-side by the Crew Pack Bundle
 * automatic discount (Shopify Function) once items are in the cart. This
 * module only adds plain line items — it never sends prices or discounts.
 *
 * Tier data comes from the element's data-tiers attribute, which the snippet
 * renders from the bundle config metafield (single source of truth).
 */
import Cart from 'cart-store';

// Per-row quantity cap (Peter 2026-09-17: keep 9). One constant drives the
// clamp in setQty, the plus button's disabled state and the visible note.
// Larger orders go through the wholesale portal.
const MAX_QTY = 9;

class HysBundleBuilder extends HTMLElement {
  connectedCallback() {
    if (this.__init) return;
    this.__init = true;

    try {
      this.tiers = JSON.parse(this.dataset.tiers || '[]')
        .filter((t) => t && typeof t.min_qty === 'number' && typeof t.value === 'number')
        .sort((a, b) => a.min_qty - b.min_qty);
    } catch (e) {
      this.tiers = [];
    }

    /* Extra-pair offer (2026-10-21): data-extra = {"pct":20,"max":3} when the
     * offer is on. Estimate = pct% off the cheapest `max` units after the
     * first (most expensive) pair, floored per unit like Shopify. */
    try {
      const ex = this.dataset.extra ? JSON.parse(this.dataset.extra) : null;
      this.extra = ex && Number(ex.pct) > 0 && Number(ex.max) > 0 ? { pct: Number(ex.pct), max: Number(ex.max) } : null;
    } catch (e) {
      this.extra = null;
    }

    this.currency = this.dataset.currency || 'CAD';
    this.fmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: this.currency });
    // match the store's Liquid money format ("$46.99", not "CA$46.99")
    this.money = (cents) => this.fmt.format(cents / 100).replace(/^CA\$/, '$');

    this.totalEl = this.querySelector('[data-total]');
    this.statusEl = this.querySelector('[data-status]');
    this.thumbsEl = this.querySelector('[data-thumbs]');
    this.addBtn = this.querySelector('[data-add]');
    // page mode only: "Added items" sidebar list
    this.addedListEl = this.querySelector('[data-added-list]');
    this.addedCountEl = this.querySelector('[data-added-count]');
    this.addedEmptyEl = this.querySelector('[data-added-empty]');

    this.addEventListener('click', (e) => {
      const remove = e.target.closest('[data-remove-index]');
      if (remove) {
        const row = (this.__selectedRows || [])[Number(remove.dataset.removeIndex)];
        if (row) this.setQty(row, 0);
        return;
      }

      const option = e.target.closest('[data-variant-option]');
      if (option) return this.chooseVariant(option);

      const rowAdd = e.target.closest('[data-row-add]');
      if (rowAdd) return this.rowAdd(rowAdd.closest('[data-item]'));

      const chosen = e.target.closest('[data-chosen]');
      if (chosen) return this.toggleMenu(chosen.closest('[data-item]'), true);

      const stepBtn = e.target.closest('[data-step]');
      if (stepBtn) {
        const row = stepBtn.closest('[data-item]');
        return this.setQty(row, this.qtyOf(row) + Number(stepBtn.dataset.step));
      }
      if (e.target.closest('[data-add]')) this.addToCart();
    });

    // close any open variant menu when clicking elsewhere
    this.__outsideClose = (e) => {
      if (!e.target.closest('[data-action]')) this.closeMenus();
    };
    document.addEventListener('click', this.__outsideClose);

    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.__outsideClose);
  }

  /* --- per-row state ------------------------------------------------------ */

  qtyOf(row) {
    return parseInt(row.querySelector('[data-qty]').textContent, 10) || 0;
  }

  priceOf(row) {
    return parseInt(row.dataset.price, 10) || 0;
  }

  /* --- variant popover (FastBundle pattern) -------------------------------- */

  rowAdd(row) {
    const menu = row.querySelector('[data-variant-menu]');
    if (menu) {
      this.toggleMenu(row, menu.hidden);
    } else {
      this.setQty(row, 1);
    }
  }

  toggleMenu(row, open) {
    this.closeMenus();
    const menu = row.querySelector('[data-variant-menu]');
    if (menu) menu.hidden = !open;
  }

  closeMenus() {
    this.querySelectorAll('[data-variant-menu]').forEach((m) => (m.hidden = true));
  }

  chooseVariant(option) {
    const row = option.closest('[data-item]');
    row.dataset.variantId = option.dataset.variantId;
    row.dataset.price = option.dataset.price;
    const chosen = row.querySelector('[data-chosen]');
    chosen.textContent = option.textContent;
    chosen.hidden = false;
    row.querySelector('[data-row-price]').textContent = this.money(this.priceOf(row));
    this.closeMenus();
    this.setQty(row, Math.max(1, this.qtyOf(row)));
  }

  setQty(row, qty) {
    const next = Math.max(0, Math.min(MAX_QTY, qty));
    row.querySelector('[data-qty]').textContent = String(next);
    // At the cap the plus button used to accept clicks and silently do
    // nothing (audit F06). Native `disabled` blocks clicks AND keys, which
    // aria-disabled alone would not; the note says why.
    const plus = row.querySelector('[data-step="1"]');
    if (plus) {
      const atMax = next >= MAX_QTY;
      // A focused button that becomes disabled drops focus to <body>, which
      // strands keyboard users; hand focus to the minus button instead.
      if (atMax && document.activeElement === plus) {
        const minus = row.querySelector('[data-step="-1"]');
        if (minus) minus.focus();
      }
      plus.disabled = atMax;
    }
    const maxNote = row.querySelector('[data-qty-max]');
    if (maxNote) {
      maxNote.textContent = next >= MAX_QTY ? `Max ${MAX_QTY} per style` : '';
      maxNote.hidden = next < MAX_QTY;
    }
    // Add button <-> stepper swap (FastBundle pattern)
    row.querySelector('[data-row-add]').hidden = next > 0;
    row.querySelector('[data-stepper]').hidden = next === 0;
    row.classList.toggle('is-selected', next > 0);
    if (next === 0) {
      const chosen = row.querySelector('[data-chosen]');
      if (chosen) chosen.hidden = true;
    }
    this.render();
  }

  /* --- estimate + status (preview only) ------------------------------------- */

  tierFor(units) {
    let match = null;
    for (const tier of this.tiers) if (units >= tier.min_qty) match = tier;
    return match;
  }

  nextTier(units) {
    return this.tiers.find((t) => units < t.min_qty) || null;
  }

  render() {
    const rows = Array.from(this.querySelectorAll('[data-item]'));
    const selected = rows.filter((row) => this.qtyOf(row) > 0);
    const units = selected.reduce((sum, row) => sum + this.qtyOf(row), 0);
    const subtotal = selected.reduce((sum, row) => sum + this.qtyOf(row) * this.priceOf(row), 0);
    const tier = this.extra ? null : this.tierFor(units);
    const next = this.extra ? null : this.nextTier(units);
    let saving = tier ? Math.round((subtotal * tier.value) / 100) : 0;
    if (this.extra) {
      // every selected unit's price, most expensive first; the first stays full price
      const prices = selected
        .flatMap((row) => Array(this.qtyOf(row)).fill(this.priceOf(row)))
        .sort((a, b) => b - a);
      saving = prices
        .slice(1)
        .sort((a, b) => a - b)
        .slice(0, this.extra.max)
        .reduce((sum, p) => sum + Math.floor((p * this.extra.pct) / 100), 0);
    }

    this.querySelectorAll('[data-tier-min]').forEach((card) => {
      const active = this.extra
        ? units >= Number(card.dataset.tierMin)
        : tier ? Number(card.dataset.tierMin) === tier.min_qty : false;
      card.classList.toggle('is-active', active);
    });

    this.totalEl.textContent = this.money(subtotal - saving);
    this.addBtn.disabled = units === 0;

    // selected-item thumbnails in the total bar (FastBundle pattern)
    const MAX_THUMBS = 3;
    this.thumbsEl.innerHTML = '';
    selected.slice(0, MAX_THUMBS).forEach((row) => {
      const img = document.createElement('img');
      img.src = row.dataset.thumb;
      img.alt = '';
      img.loading = 'lazy';
      this.thumbsEl.appendChild(img);
    });
    if (selected.length > MAX_THUMBS) {
      const more = document.createElement('span');
      more.className = 'hys-bundle-builder__thumbs-more';
      more.textContent = `+${selected.length - MAX_THUMBS}`;
      this.thumbsEl.appendChild(more);
    }

    // page mode: "Added items" sidebar list
    if (this.addedListEl) {
      this.__selectedRows = selected;
      if (this.addedCountEl) this.addedCountEl.textContent = `${units} Added`;
      if (this.addedEmptyEl) this.addedEmptyEl.hidden = selected.length > 0;
      this.addedListEl.querySelectorAll('.hys-bundle-builder__added-item').forEach((el) => el.remove());
      selected.forEach((row, i) => {
        const entry = document.createElement('div');
        entry.className = 'hys-bundle-builder__added-item';

        const img = document.createElement('img');
        img.src = row.dataset.thumb;
        img.alt = '';
        entry.appendChild(img);

        const label = document.createElement('span');
        label.className = 'hys-bundle-builder__added-title';
        const chosen = row.querySelector('[data-chosen]');
        label.textContent =
          (row.dataset.title || '') + (chosen && !chosen.hidden ? ` — ${chosen.textContent}` : '');
        entry.appendChild(label);

        const qty = document.createElement('span');
        qty.className = 'hys-bundle-builder__added-qty';
        qty.textContent = `×${this.qtyOf(row)}`;
        entry.appendChild(qty);

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'hys-bundle-builder__added-remove';
        remove.setAttribute('data-remove-index', String(i));
        remove.setAttribute('aria-label', `Remove ${row.dataset.title || 'item'}`);
        remove.textContent = '×';
        entry.appendChild(remove);

        this.addedListEl.appendChild(entry);
      });
    }

    if (this.extra) {
      const { pct, max } = this.extra;
      if (units === 0) this.statusEl.textContent = 'No items added!';
      else if (units === 1) this.statusEl.textContent = `Add a 2nd pair to save ${pct}% on it`;
      else if (units <= max) this.statusEl.textContent = `You save ${this.money(saving)}. Add 1 more to save ${pct}% on it too`;
      else this.statusEl.textContent = `You save ${this.money(saving)}, the max (${max} extra pairs at ${pct}% off)`;
    } else if (units === 0) {
      this.statusEl.textContent = 'No items added!';
    } else if (tier && !next) {
      this.statusEl.textContent = `You save ${this.money(saving)} (${tier.value}%)`;
    } else if (tier && next) {
      this.statusEl.textContent = `You save ${this.money(saving)} — add ${next.min_qty - units} more for ${next.value}%`;
    } else if (next) {
      this.statusEl.textContent = `Add ${next.min_qty - units} more to save ${next.value}%`;
    } else {
      this.statusEl.textContent = '';
    }
  }

  /* --- cart -------------------------------------------------------------------- */

  async addToCart() {
    const lines = Array.from(this.querySelectorAll('[data-item]'))
      .filter((row) => this.qtyOf(row) > 0)
      .map((row) => ({ id: row.dataset.variantId, quantity: this.qtyOf(row) }));
    if (!lines.length) return;

    this.addBtn.disabled = true;
    const label = this.addBtn.textContent;
    this.addBtn.textContent = 'Adding…';

    try {
      await Cart.add(lines);
      this.querySelectorAll('[data-item]').forEach((row) => this.setQty(row, 0));
      const drawer = document.querySelector('.modal--cart, .popup--cart-notification');
      if (drawer && !drawer.shown && typeof drawer.open === 'function') drawer.open();
    } catch (error) {
      this.statusEl.textContent = 'Could not add to cart — please try again.';
    } finally {
      this.addBtn.textContent = label;
      this.render();
    }
  }
}

if (!customElements.get('hys-bundle-builder')) {
  customElements.define('hys-bundle-builder', HysBundleBuilder);
}
